import React, { useMemo, useRef, useState } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { formatCurrencyNumber } from '@/components/Input/helpers';
import { Product } from '@/context/types';
import { CookieIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ORDER, PRODUCT } from '@/constants';

export default ({ box, isEven }: { box: Product; isEven: boolean }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // NEW: Track hover state to orchestrate the color inversions
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();

  // 1. DYNAMIC THEMING: Updated with solid colors and a contrast flag
  const theme = useMemo(() => {
    const options = [
      {
        name: 'truffle',
        bg: '#3d2012', // Solid Truffle
        accent: '#3d2012',
        text: 'text-brand-truffle',
        isDark: true, // Needs white text on hover
      },
      {
        name: 'gold',
        bg: '#fac547', // Solid Gold
        accent: '#855e00',
        text: 'text-[#855e00]',
        isDark: false, // Light enough to keep dark text on hover
      },
      {
        name: 'burgundy',
        bg: '#721e1e', // Solid Burgundy
        accent: '#721e1e',
        text: 'text-brand-burgundy',
        isDark: true, // Needs white text on hover
      },
      {
        name: 'frost',
        bg: '#bfd2db', // Solid Frost
        accent: '#4a5e6d',
        text: 'text-[#4a5e6d]',
        isDark: false, // Light enough to keep dark text on hover
      },
    ];
    return options[Math.floor(Math.random() * options.length)];
  }, []);

  // 2. MOTION PHYSICS: 3D Tilt Setup
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const mouseXSpring = useSpring(x, { stiffness: 150, damping: 20 });
  const mouseYSpring = useSpring(y, { stiffness: 150, damping: 20 });

  const rotateX = useTransform(mouseYSpring, [-0.5, 0.5], ['7deg', '-7deg']);
  const rotateY = useTransform(mouseXSpring, [-0.5, 0.5], ['-7deg', '7deg']);

  const imageX = useTransform(mouseXSpring, [-0.5, 0.5], [15, -15]);
  const imageY = useTransform(mouseYSpring, [-0.5, 0.5], [15, -15]);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const xPct = mouseX / width - 0.5;
    const yPct = mouseY / height - 0.5;

    x.set(xPct);
    y.set(yPct);
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    x.set(0);
    y.set(0);
  };

  // 3. TEXT COLOR HELPERS: Dynamically determine text colors based on hover + theme contrast
  const shouldInvertText = isHovered && theme.isDark;

  const headingColor = shouldInvertText ? 'text-white' : 'text-text-heading';
  const mutedColor = shouldInvertText ? 'text-white/80' : 'text-text-muted';
  const labelColor = shouldInvertText ? 'text-white' : theme.text;

  return (
    <motion.div
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        rotateX,
        rotateY,
        transformStyle: 'preserve-3d',
      }}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-100px' }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`group relative flex flex-col items-center gap-12 md:gap-20 p-8 md:p-16 rounded-[3rem] bg-white cursor-pointer mb-12 ${
        isEven ? 'md:flex-row' : 'md:flex-row-reverse'
      } shadow-[0_20px_50px_rgba(0,0,0,0.03)] hover:shadow-[0_80px_120px_-20px_rgba(0,0,0,0.2)]`}
    >
      {/* BACKGROUND COLOR WASH - Now transitions to solid on hover */}
      <motion.div
        className="absolute inset-0 rounded-[3rem] pointer-events-none"
        animate={{
          backgroundColor: isHovered ? theme.bg : '#ffffff',
        }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
      />

      {/* IMAGE CONTAINER with Parallax */}
      <div className="w-full md:w-1/2 perspective-[1200px]">
        <motion.div
          style={{
            x: imageX,
            y: imageY,
            z: '50px',
          }}
          className="relative rounded-[2.5rem] overflow-visible"
        >
          <img
            src={box?.img?.url}
            alt={box?.img?.altText || box.title}
            className="w-full aspect-[3/3] object-contain scale-110 group-hover:scale-125 transition-transform duration-[2s] ease-expo drop-shadow-2xl "
          />
          <div className="absolute inset-0 ring-1 ring-inset ring-black/5 rounded-[2.5rem]" />
        </motion.div>
      </div>

      {/* CONTENT AREA */}
      <div className="w-full md:w-1/2 flex flex-col items-center md:items-start text-center md:text-left z-20">
        <motion.span
          style={{ z: '30px' }}
          className={`text-[10px] font-black tracking-[0.4em] uppercase mb-6 transition-colors duration-300 ${labelColor}`}
        >
          {box.productType || 'Artisanal Collection'}
        </motion.span>

        <motion.h2
          style={{ z: '40px' }}
          className={`font-display font-bold text-5xl md:text-7xl leading-[0.9] tracking-tighter mb-6 transition-colors duration-300 ${headingColor}`}
        >
          {box.title}
        </motion.h2>

        <motion.div
          style={{ z: '20px' }}
          className={`font-body text-lg leading-relaxed mb-10 opacity-80 group-hover:opacity-100 transition-all duration-300 ${mutedColor}`}
          dangerouslySetInnerHTML={{ __html: box.description as string }}
        />

        {/* FOOTER ACTION */}
        <div
          className={`w-full flex flex-col sm:flex-row items-center gap-8 pt-10 border-t transition-colors duration-300 ${shouldInvertText ? 'border-white/20' : 'border-brand-truffle/5'}`}
        >
          <div className="flex flex-col">
            <span
              className={`text-[10px] uppercase tracking-[0.2em] mb-1 transition-colors duration-300 ${shouldInvertText ? 'text-white/60' : 'text-text-muted'}`}
            >
              Price
            </span>
            <p
              className={`font-display font-bold text-4xl transition-colors duration-300 ${headingColor}`}
            >
              {formatCurrencyNumber(box.price)}
            </p>
          </div>

          <motion.button
            animate={{
              // Flips the button styling when the box is hovered
              backgroundColor: isHovered ? '#ffffff' : theme.accent,
              color: isHovered ? theme.accent : '#ffffff',
              borderColor: isHovered ? '#ffffff' : theme.accent,
            }}
            style={{
              z: '60px',
              borderWidth: '2px',
              borderStyle: 'solid',
              cursor: 'pointer',
            }}
            className="flex items-center gap-4 px-10 py-5 rounded-full font-display font-bold text-[13px] tracking-[0.2em] uppercase shadow-xl group-hover:shadow-2xl transition-shadow whitespace-nowrap ml-auto"
            onClick={() => navigate(`/product/${box.numericId}`)}
            role="button"
          >
            <CookieIcon size={18} />
            <span>Details</span>
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};
